import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { useUserContext } from "./UserContext";
import { AddOrUpdateUserCart, GetUserCart } from '../Services/e-pro/EcatalogService';
// Types and Interfaces
interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  [key: string]: any; // For additional product properties
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalUniqueItems: number;
  cartTotal: number;
}

interface CartContextType extends CartState {
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (itemId: string) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  removeItems: (itemIds: string[]) => void;
}

interface CartProviderProps {
  children: React.ReactNode;
  userId?: string;
}

// Action types
type CartAction =
  | { type: 'SET_ITEMS'; payload: CartItem[] }
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'REMOVE_ITEMS'; payload: string[] };

// Database service interface
interface DbService {
  fetchCart: (empId: number, empCode: string) => Promise<{ items: CartItem[] } | null>;
  saveCart: (empId: number, empCode: string, cartState: CartState) => Promise<void>;
}

// Initial state
const initialState: CartState = {
  items: [],
  totalItems: 0,
  totalUniqueItems: 0,
  cartTotal: 0,
};

// Create context with type
const CartContext = createContext<CartContextType | undefined>(undefined);

// Database service implementation
const dbService: DbService = {
  async fetchCart(empId: number, empCode: string) {
    // Implement your database fetch logic
    // Example: return await db.collection('carts').findOne({ userId });
    let userCart = await GetUserCart({EmployeeId: empId, EmployeeCode : empCode, ProductDetails: ""});
    let returnObject = {
      items: userCart.data != undefined ? JSON.parse(userCart.data.ProductDetails) : []
    }
    return returnObject;
  },
  
  async saveCart(empId: number, empCode: string, cartState: CartState) {
    // Implement your database save logic
    // Example: await db.collection('carts').updateOne(
    //   { userId },
    //   { $set: cartState },
    //   { upsert: true }
    // );
    let userCart = await AddOrUpdateUserCart({EmployeeId: empId, EmployeeCode : empCode, ProductDetails: JSON.stringify(cartState.items)});

    console.log('Saving cart to DB:', cartState);
  }
};

// Helper function to calculate cart totals
const calculateCartState = (items: CartItem[]): CartState => {
  return {
    items,
    totalItems: items.reduce((sum, item) => sum + item.quantity, 0),
    totalUniqueItems: items.length,
    cartTotal: items.reduce((sum, item) => sum + item.quantity * item.price, 0),
  };
};

// Reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
  let newState: CartState;
  switch (action.type) {
    case 'SET_ITEMS':
      //return calculateCartState(action.payload);
      newState = calculateCartState(action.payload);
      break;
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(item => item.id === action.payload.id);
      let newItems: CartItem[];
      
      if (existingItemIndex > -1) {
        newItems = state.items.map((item, index) => {
          if (index === existingItemIndex) {
            return {
              ...item,
              quantity: item.quantity + action.payload.quantity
            };
          }
          return item;
        });
      } else {
        newItems = [...state.items, action.payload];
      }
      
      //return calculateCartState(newItems);
      newState = calculateCartState(newItems);
      break;
    }
    
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      //return calculateCartState(newItems);
      newState = calculateCartState(newItems);
      break;
    }
    
    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item => {
        if (item.id === action.payload.id) {
          return { ...item, quantity: action.payload.quantity };
        }
        return item;
      });
      //return calculateCartState(newItems);
      newState = calculateCartState(newItems);
      break;
    }
    
    case 'CLEAR_CART':
      //return initialState;
      newState = calculateCartState(initialState);
      break;

    case 'REMOVE_ITEMS': {
      const newItems = state.items.filter(item => !action.payload.includes(item.id));
      newState = calculateCartState(newItems);
      break;
    }
    default:
      return state;
  }

  // Save to localStorage after every state change
  //localStorage.setItem('cart', JSON.stringify(newState));
  return newState;
};

// Provider component
export const CartProvider: React.FC<CartProviderProps> = ({ children/*, userId*/ }) => {
  const [userData, setUserData] = useUserContext();
  const [state, dispatch] = useReducer(cartReducer, initialState);
//   const [state, dispatch] = useReducer(cartReducer, initialState, () => {
//     const savedCart = localStorage.getItem('cart');
//     return savedCart ? JSON.parse(savedCart) : initialState;
//   });
//  // Force update mechanism
//  const [, setForceUpdate] = useState(0)

  let userId = userData.EmployeeCode;
  // Load initial cart data
  useEffect(() => {
    const loadCart = async () => {
      try {
        const savedCart = await dbService.fetchCart(userData.EmployeeId, userData.EmployeeCode);
        if (savedCart) {
          dispatch({ type: 'SET_ITEMS', payload: savedCart.items });
        }
      } catch (error) {
        console.error('Error loading cart:', error);
      }
    };

    if (userId) {
      loadCart();
    }
  }, [userId]);

  // Save cart updates to database
  useEffect(() => {
    const saveCart = async () => {
      try {
        await dbService.saveCart(userData.EmployeeId, userData.EmployeeCode, state);
      } catch (error) {
        console.error('Error saving cart:', error);
      }
    };

    if (userId && state !== initialState) {
      saveCart();
    }
  }, [state, userId]);

  // Cart operations
  const addItem = (item: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: { ...item, quantity }
    });
    //setForceUpdate(prev => prev + 1); // Force update
  };

  const removeItem = (itemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: itemId });
    //setForceUpdate(prev => prev + 1); // Force update
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    dispatch({
      type: 'UPDATE_QUANTITY',
      payload: { id: itemId, quantity }
    });
    //setForceUpdate(prev => prev + 1); // Force update
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    //setForceUpdate(prev => prev + 1); // Force update
  };

  const removeItems = (itemIds: string[]) => {
    dispatch({ type: 'REMOVE_ITEMS', payload: itemIds });
  };

  const value: CartContextType = {
    ...state,
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart,
    removeItems,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

